import { useState } from "react";

export function TrendChart({
  values,
  label,
  pointLabels = [],
  rangeStartLabel = "НАЧАЛО ПЕРИОДА",
  rangeEndLabel = "СЕЙЧАС",
  unitLabel = "КЛИЕНТОВ",
  valueFormatter = (value) => value.toLocaleString("ru-RU")
}: {
  values: number[];
  label: string;
  pointLabels?: string[];
  rangeStartLabel?: string;
  rangeEndLabel?: string;
  unitLabel?: string;
  valueFormatter?: (value: number) => string;
}) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const width = 700;
  const height = 190;
  const max = Math.max(1, ...values);
  const coordinates =
    values.length <= 1
      ? [
          {
            x: width / 2,
            y: height - ((values[0] ?? 0) / max) * (height - 16) - 8,
            value: values[0] ?? 0
          }
        ]
      : values.map((value, index) => ({
          x: (index / (values.length - 1)) * width,
          y: height - (value / max) * (height - 16) - 8,
          value
        }));
  const polylinePoints = coordinates
    .map((point) => `${point.x},${point.y}`)
    .join(" ");
  const hoveredPoint =
    hoveredIndex === null ? null : coordinates[hoveredIndex] ?? null;

  return (
    <div className="trend-chart">
      <div className="chart-scale">
        <span>{valueFormatter(max)}</span>
        <span>{valueFormatter(max / 2)}</span>
        <span>{valueFormatter(0)}</span>
      </div>
      <div className="trend-chart__plot">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={label}
        >
          <line x1="0" y1="8" x2={width} y2="8" />
          <line x1="0" y1={height / 2} x2={width} y2={height / 2} />
          <line x1="0" y1={height - 1} x2={width} y2={height - 1} />
          <polyline points={polylinePoints} />
          {coordinates.map((point, index) => (
            <g key={`${point.x}-${index}`}>
              <circle
                className="trend-chart__hit"
                cx={point.x}
                cy={point.y}
                r="12"
                tabIndex={0}
                aria-label={`${pointLabels[index] ?? `Бакет ${index + 1}`}: ${valueFormatter(point.value)} ${unitLabel.toLowerCase()}`}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                onFocus={() => setHoveredIndex(index)}
                onBlur={() => setHoveredIndex(null)}
              />
              <circle
                className="trend-chart__point"
                cx={point.x}
                cy={point.y}
                r={hoveredIndex === index ? 5 : 3.5}
              />
            </g>
          ))}
        </svg>
        {hoveredPoint ? (
          <div
            className="trend-chart__tooltip"
            style={{
              left: `${(hoveredPoint.x / width) * 100}%`,
              top: `${(hoveredPoint.y / height) * 100}%`
            }}
          >
            <strong>{valueFormatter(hoveredPoint.value)}</strong>
            <span>{unitLabel}</span>
            <small>{pointLabels[hoveredIndex ?? 0] ?? label}</small>
          </div>
        ) : null}
      </div>
      <div className="chart-caption">
        <span>{rangeStartLabel}</span>
        <span>{label}</span>
        <span>{rangeEndLabel}</span>
      </div>
    </div>
  );
}
